/**
 * Service Layer Auth Session Management
 * 
 * Handles session fetching, retry logic, and URL sanitization.
 */

import React from 'react';
import { getFreshServiceLayerAccessToken } from '@appic/service-layer';
import { authClient } from './authClient';
import { startLookupBound, withinLookupBound } from './authSessionLookupBound';

// The bound and its error are part of this module's surface for callers and tests.
export { BEARER_LOOKUP_TIMEOUT_MS, BearerLookupTimeoutError } from './authSessionLookupBound';

export interface SessionState {
  setEmail: (email: string | null) => void;
  setRole: (role: string | null) => void;
  setCsrfToken: (token: string | null) => void;
}

/**
 * Obtain a non-stale Service-Layer access token from the canonical SL Supabase
 * client (storageKey `sb-clanda-sl`, `autoRefreshToken: true`) owned by the
 * `@appic/service-layer` package.
 *
 * Why this exists: `serviceFetch` reads the access_token RAW from
 * localStorage to build the `Authorization: Bearer` fallback header. A raw
 * read NEVER triggers Supabase auto-refresh, so after a long-idle tab an
 * EXPIRED JWT keeps being sent and the SL returns degraded/empty 200s or 403s
 * — surfacing as silent blank pages
 * (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31, the recurring
 * "Steffen-Friday" incident).
 *
 * `getFreshServiceLayerAccessToken` routes through the SL client's
 * `auth.getSession()` (which auto-refreshes a token at/near expiry against the
 * `sb-clanda-sl` store) and, failing that, `auth.refreshSession()`. It returns
 * the fresh `access_token`, or `null` when no session can be re-established
 * (signed out / refresh token revoked). Callers MUST treat `null` as "session
 * is gone" and signal loudly — never silently degrade (D-33).
 *
 * The SL Supabase client is a cached singleton; we pass the same Supabase
 * credentials `authClient` was constructed with (read once from Vite env), so
 * this resolves the SAME client instance that holds the live SL session.
 *
 * Lives here (not in serviceLayerClient) so serviceLayerClient depends on this
 * module rather than the reverse — no import cycle (authSession does not import
 * serviceLayerClient; the SL package does not import serviceLayerClient).
 */
/**
 * Single shared in-flight refresh promise.
 *
 * BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31 (P0): on a page that
 * fires many parallel SL requests (missions + leads + registry + activity,
 * etc.), every request that finds a stale/near-expiry raw token in
 * localStorage independently called `getFreshServiceLayerAccessToken()` →
 * each fired `supabase.auth.refreshSession()` → the SECOND+ concurrent
 * refresh reused the SAME (already-consumed) Supabase refresh token →
 * "AuthApiError: Invalid Refresh Token: Already Used" + a 401 on whichever
 * request lost the race (observed on `/service/v1/scout/activity`). That 401
 * then drove the session-expired path and the empty mission render
 * (BUG-FE-MISSION-DATA-ZERO-VISIBLE-2026-05-31).
 *
 * De-dupe so concurrent callers share ONE refresh round-trip: the first
 * caller starts it, every overlapping caller awaits the same promise, and
 * the slot clears once it settles so the NEXT (later) stale window can
 * refresh again. This serialises refresh-token consumption → no
 * "already used".
 */
let inFlightRefresh: Promise<string | null> | null = null;

export async function getFreshAccessToken(): Promise<string | null> {
  // Coalesce overlapping refreshes onto a single Supabase refresh round-trip. The slot is held until
  // the lookup SETTLES, also past its bound: starting a second lookup beside a merely slow one would
  // spend the same refresh token twice ("already used", above). Each caller's wait is what is bounded
  // (authSessionLookupBound), measured from when the lookup in the slot started.
  if (inFlightRefresh) return withinLookupBound(inFlightRefresh);

  startLookupBound();
  const refresh = getFreshServiceLayerAccessToken({
    // serviceLayerBaseUrl is a required field of ServiceLayerAuthConfig; this
    // call only exercises the Supabase-session path, but the shared config type
    // demands it, so source it from the same Vite env the SL client uses
    // (default mirrors resolveApiBase's "/service" reverse-proxy base).
    serviceLayerBaseUrl: import.meta.env.VITE_SERVICE_LAYER_BASE_URL || '/service',
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  })
    // Clear the slot regardless of outcome so a later request (after this
    // token in turn ages out) can refresh again. `finally` keeps both the
    // resolve and reject paths from leaking a stale shared promise.
    .finally(() => {
      inFlightRefresh = null;
    });

  inFlightRefresh = refresh;
  // Every waiter may already have left at the bound when the lookup finally rejects; that late
  // rejection has no one to go to and must not surface as an unhandled rejection.
  refresh.catch(() => undefined);
  return withinLookupBound(refresh);
}

/**
 * Fetch session from Service Layer with retry logic
 */
export async function fetchSession(
  state: SessionState,
  retryCount = 0,
  signal?: AbortSignal
): Promise<void> {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000; // 1 second (capped linear backoff)
  
  // Check if aborted before proceeding
  if (signal?.aborted) {
    return;
  }
  
  try {
    // Use auth client package to get session
    // Check if aborted before proceeding
    if (signal?.aborted) {
      return;
    }
    
    const data = await authClient.getSession();

    // Check if aborted before state updates
    if (signal?.aborted) {
      return;
    }
    
    const userEmail = data.user?.email ?? null;
    const userRole = data.user?.role ?? null; // Role from JWT token
    
    // Store CSRF token from session response (for mutating requests)
    if (data.csrf_token) {
      state.setCsrfToken(data.csrf_token);
    }
    
    if (userEmail) {
      state.setEmail(userEmail);
      // Store role from session (available immediately, no API call needed!)
      if (userRole) {
        state.setRole(userRole);
      }
    } else {
      if (!signal?.aborted) {
        state.setRole(null); // Clear role if no email
        state.setEmail(null);
        state.setCsrfToken(null); // Clear CSRF if not authenticated
      }
      throw new Error('Session response missing user information');
    }
  } catch (error) {
    // Don't process errors if aborted
    if (signal?.aborted) {
      return;
    }
    
    // Network errors - retry if possible
    if (error instanceof TypeError && error.message.includes('fetch') && retryCount < MAX_RETRIES) {
      console.warn(`[ServiceLayerAuth] Network error, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
      return fetchSession(state, retryCount + 1, signal);
    }
    
    // AbortError - component unmounted, don't update state
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    
    // Log session fetch failure
    if (error instanceof Error) {
      console.warn('[ServiceLayerAuth] Failed to fetch session:', error.message);
    } else {
      console.warn('[ServiceLayerAuth] Failed to fetch session:', error);
    }
    
    if (!signal?.aborted) {
      state.setEmail(null);
    }
    throw error;
  }
}

/**
 * Create a safe wrapper for fetchSession that prevents concurrent calls
 */
export function createSafeFetchSession(
  state: SessionState,
  inFlightRef: React.MutableRefObject<boolean>
) {
  return async (signal?: AbortSignal): Promise<void> => {
    if (inFlightRef.current) {
      return; // Already in flight, skip
    }
    inFlightRef.current = true;
    try {
      await fetchSession(state, 0, signal);
    } finally {
      inFlightRef.current = false;
    }
  };
}

/**
 * Sanitize URL by removing trailing slashes and query params
 * Prevents cache key issues in some SPAs
 * Explicitly sanitizes URL once per flow
 * Debounced to avoid rapid calls during SPA transitions
 */
export function createSanitizeUrl(sanitizeUrlTimeoutRef: React.MutableRefObject<number | null>) {
  return () => {
    // Clear any pending sanitization
    if (sanitizeUrlTimeoutRef.current !== null) {
      window.clearTimeout(sanitizeUrlTimeoutRef.current);
    }
    
    // Debounce: wait 100ms before sanitizing (handles rapid SPA transitions)
    sanitizeUrlTimeoutRef.current = window.setTimeout(() => {
      try {
        const cleanPath = window.location.pathname.replace(/\/+$/, '') || '/';
        window.history.replaceState({}, document.title, cleanPath);
      } catch {
        // Ignore errors (e.g., in test environments)
      } finally {
        sanitizeUrlTimeoutRef.current = null;
      }
    }, 100);
  };
}

